import { useState } from 'react'
import { CheckCircle2, Skull } from 'lucide-react'

import api from '@/services/api'

/**
 * Mark every current deal Dead for the parallel-run reset (LPR-22 / MOD-28).
 *
 * The 24 Sep decision was Dead, not deleted: each deal keeps its record, stays
 * searchable and can be reopened. The server retires them with a housekeeping
 * reason (never a business kill reason, so lost-deal reporting is not
 * polluted) and records one audit event. The count is previewed first and must
 * be typed back, so the batch is exactly what the person saw.
 */
const REASON = 'parallel_run_reset'

type Preview = { count: number; reasonLabel?: string }

function refusal(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail
  return typeof detail === 'string' && detail.trim() ? detail : fallback
}

export default function BulkRetirePanel() {
  const [preview, setPreview] = useState<Preview | null>(null)
  const [note, setNote] = useState('')
  const [typedCount, setTypedCount] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<number | null>(null)

  const loadPreview = async () => {
    setBusy(true)
    setError(null)
    setDone(null)
    try {
      const res = await api.post('/loans/bulk-kill', {
        reason_code: REASON,
        note: '',
        all_active: true,
        dry_run: true,
      })
      const data = res.data as { count?: number; reasonLabel?: string }
      setPreview({ count: Number(data.count ?? 0), reasonLabel: data.reasonLabel })
      setTypedCount('')
    } catch (err) {
      setError(refusal(err, 'Could not count the active deals.'))
    } finally {
      setBusy(false)
    }
  }

  const retire = async () => {
    if (!preview) return
    setBusy(true)
    setError(null)
    try {
      const res = await api.post('/loans/bulk-kill', {
        reason_code: REASON,
        note: note.trim(),
        all_active: true,
        expected_count: preview.count,
      })
      const killed = (res.data as { killed?: string[] }).killed ?? []
      setDone(killed.length)
      setPreview(null)
      setNote('')
      setTypedCount('')
    } catch (err) {
      setError(refusal(err, 'The deals could not be marked Dead. Nothing was changed.'))
    } finally {
      setBusy(false)
    }
  }

  const ready =
    preview !== null && preview.count > 0 && note.trim().length > 0 && typedCount.trim() === String(preview.count)

  return (
    <div className="space-y-3 rounded-lg border border-amber-400/20 bg-amber-400/[0.04] p-4">
      <div className="flex items-start gap-3">
        <Skull size={18} className="mt-0.5 text-amber-300" />
        <div>
          <div className="text-[13px] font-medium text-stone-100">Mark every current deal Dead (parallel-run reset)</div>
          <p className="mt-1 text-[11px] leading-relaxed text-stone-400">
            Retires every active pipeline deal (funded loans are left alone) with the housekeeping reason{' '}
            <span className="text-stone-200">Parallel-run reset</span> — not a lost-deal reason, and no broker
            attestation. Deals are <span className="text-stone-200">Dead, not deleted</span>: they stay searchable, keep
            their history, and can be reopened one by one.
          </p>
        </div>
      </div>
      {error ? <div className="text-[11px] text-red-300">{error}</div> : null}
      {done !== null ? (
        <div className="flex items-center gap-1 text-[11px] text-emerald-400">
          <CheckCircle2 size={12} /> Marked {done} deal(s) Dead.
        </div>
      ) : null}
      <button
        type="button"
        className="rounded border border-white/10 px-3 py-1.5 text-[12px] text-stone-200 hover:bg-white/5 disabled:opacity-40"
        disabled={busy}
        onClick={() => void loadPreview()}
      >
        Count active deals
      </button>
      {preview ? (
        <div className="space-y-2">
          <div className="text-[12px] text-stone-200">
            {preview.count} active deal(s) will be marked Dead
            {preview.reasonLabel ? ` — ${preview.reasonLabel}` : ''}.
          </div>
          <label className="block text-[10px] text-stone-500">
            Why (recorded on every deal and in the audit log)
            <input
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="e.g. 24 Sep decision: current pipeline retired before the parallel run"
              className="mt-1 block w-full max-w-lg rounded border border-white/10 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 placeholder:text-stone-600 focus:border-amber-300/40 focus:outline-none"
            />
          </label>
          <label className="block text-[10px] text-stone-500">
            Type the count to confirm
            <input
              value={typedCount}
              onChange={(event) => setTypedCount(event.target.value)}
              inputMode="numeric"
              className="mt-1 block w-24 rounded border border-white/10 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 focus:border-amber-300/40 focus:outline-none"
            />
          </label>
          <button
            type="button"
            className="rounded border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-[12px] font-medium text-amber-200 disabled:opacity-40"
            disabled={busy || !ready}
            onClick={() => void retire()}
          >
            {busy ? 'Working…' : `Mark ${preview.count} deals Dead`}
          </button>
        </div>
      ) : null}
    </div>
  )
}
